import React from "react";
import { useField } from "formik";
import { Flex } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { isUSA } from "../../../utils/isUSA";
import { FieldGroup } from "../FieldGroup";
import { MultiInputFormRow } from "../MultiInputFormRow";
import { SimpleFormRow } from "../SimpleFormRow";
import { StateSelectDropdown } from "../StateSelectDropdown";
import { countrySelectContainer } from "./styles_vanilla.css";
import { type SharedAddressInputProps } from "./types";

/**
 * AddressInput component represents an input form for capturing address information.
 *
 * @component
 * @example
 * ```tsx
 * <AddressInput
 *   title="Billing Address"
 *   description="Enter your billing address"
 *   onCountryChange={handleCountryChange}
 * />
 * ```
 *
 * @param {SharedAddressInputProps} props - The props for the SharedAddressInput component.
 * @param {string} props.title - The title of the address input form.
 * @param {string} props.description - The description of the address input form.
 * @param {React.FunctionComponent} props.CountrySelect - The country select component.
 * @param {Function} props.onCountryChange - The callback function to handle country change.
 * @param {string} [props.name=""] - The base name for the form fields.
 * @returns {React.FC<AddressInputProps>} The AddressInput component.
 *
 * @todo Ideally a significant amount of validation would occur on this form to ensure good data.
 */
export const SharedAddressInput: React.FC<SharedAddressInputProps> = ({
  title,
  description,
  CountrySelect,
  onCountryChange = undefined,
  name = "",
  testIsUSA = isUSA,
  fieldNames,
  meta,
}: SharedAddressInputProps) => {
  const fieldName = (field: string) => (name ? `${name}.${field}` : field);

  const stateFieldName = fieldNames?.state || fieldName("state");
  const countryFieldName = fieldNames?.country || fieldName("country");
  const provinceFieldName = fieldNames?.province || fieldName("province");
  const streetLine1FieldName = fieldNames?.streetLine1 || fieldName("streetLine1");
  const streetLine2FieldName = fieldNames?.streetLine2 || fieldName("streetLine2");
  const cityFieldName = fieldNames?.city || fieldName("city");
  const zipFieldName = fieldNames?.zip || fieldName("zip");

  const [, , stateHelpers] = useField<string | undefined>(stateFieldName);
  const [countryField] = useField<string | undefined>(countryFieldName);
  const [, , provinceHelpers] = useField<string | undefined>(provinceFieldName);

  const handleCountryChange = (value: string | number | string[] | number[]) => {
    if (typeof value === "string" && value !== countryField.value) {
      if (testIsUSA(value)) {
        void provinceHelpers.setValue("");
      } else {
        void stateHelpers.setValue("");
      }
    }
    onCountryChange?.();
  };
  const isCountryUSA = testIsUSA(countryField.value);

  return (
    <FieldGroup title={title} description={description} meta={meta}>
      {CountrySelect ? (
        <Flex className={countrySelectContainer}>
          <CountrySelect
            key="country"
            placeholder="Select a country"
            handleChange={handleCountryChange}
          />
        </Flex>
      ) : null}
      <SimpleFormRow>
        <FormTextInput placeholder="Street" isFullWidth name={streetLine1FieldName} />
      </SimpleFormRow>
      <SimpleFormRow>
        <FormTextInput
          placeholder="Apartment, suite, etc. (optional)"
          isFullWidth
          name={streetLine2FieldName}
        />
      </SimpleFormRow>
      <SimpleFormRow>
        <FormTextInput placeholder="City" isFullWidth name={cityFieldName} />
      </SimpleFormRow>
      <MultiInputFormRow>
        {isCountryUSA ? (
          <StateSelectDropdown key="state" placeholder="State" name={stateFieldName} />
        ) : (
          <FormTextInput
            key="province"
            isFullWidth
            name={provinceFieldName}
            placeholder="State/Province"
          />
        )}
        <FormTextInput
          key="zip"
          placeholder="ZIP/Postal code"
          isFullWidth
          name={zipFieldName}
        />
      </MultiInputFormRow>
    </FieldGroup>
  );
};
